'use client'

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import { IconSearch, IconClose } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'
import styles from './SearchBarGlobal.module.scss'

export interface SearchBarGlobalProps {
  value?: string
  placeholder?: string
  onSearch?: (query: string) => void
  onChange?: (value: string) => void
  onClose?: () => void
  onFocus?: () => void
  /** Enfoca el input al montar (por defecto `true`). */
  autoFocus?: boolean
  /** Input de solo lectura: úsalo como lanzador que abre un overlay. */
  readOnly?: boolean
  /** `dark` para fondos de color (p. ej. banda de búsqueda verde); `inverse`
   * para el header oscuro DS (borde gris, texto blanco, botón verde acento);
   * `field` para el aspecto de campo de filtro (`SelectField` inverse): cuadrado,
   * sin fondo, icono de búsqueda en blanco de línea (sin círculo relleno). */
  variant?: 'dark' | 'inverse' | 'field'
  /** Clase extra para ajustes de contexto. */
  className?: string
}

/**
 * Barra de búsqueda global (forma píldora, botón submit circular). Controlado o
 * no controlado. `forwardRef` expone el `<input>`. En `readOnly` actúa como
 * lanzador (el click/foco dispara `onFocus` para abrir el overlay de búsqueda).
 */
export const SearchBarGlobal = forwardRef<HTMLInputElement, SearchBarGlobalProps>(
  function SearchBarGlobal(
    {
      value: valueProp,
      placeholder = 'Busca por evento, ubicación o fecha',
      onSearch,
      onChange,
      onClose,
      onFocus,
      autoFocus = true,
      readOnly = false,
      variant,
      className,
    },
    ref,
  ) {
    const [internalValue, setInternalValue] = useState(valueProp ?? '')
    const inputRef = useRef<HTMLInputElement>(null)
    const isControlled = valueProp !== undefined
    const currentValue = isControlled ? valueProp : internalValue

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    useEffect(() => {
      if (autoFocus && !readOnly) inputRef.current?.focus()
    }, [autoFocus, readOnly])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return
      const val = e.target.value
      if (!isControlled) setInternalValue(val)
      onChange?.(val)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onSearch?.(currentValue)
    }

    const handleClear = () => {
      if (!isControlled) setInternalValue('')
      onChange?.('')
      inputRef.current?.focus()
    }

    return (
      <div
        className={cx(
          styles.root,
          variant === 'dark' && styles.dark,
          variant === 'inverse' && styles.inverse,
          variant === 'field' && styles.field,
          className,
        )}
        role="search"
      >
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          value={currentValue}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          readOnly={readOnly}
          aria-label="Buscar"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />

        {!readOnly && currentValue.length > 0 && (
          <button type="button" className={styles.clearButton} onClick={handleClear} aria-label="Limpiar búsqueda">
            <IconClose size={16} />
          </button>
        )}

        <button
          type="button"
          className={styles.submitButton}
          onClick={readOnly ? onFocus : () => onSearch?.(currentValue)}
          aria-label="Buscar"
          tabIndex={readOnly ? -1 : undefined}
          disabled={!readOnly && currentValue.trim() === ''}
        >
          <IconSearch size={16} />
        </button>

        {onClose && (
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancelar
          </button>
        )}
      </div>
    )
  },
)
